import { FC, useEffect, useState } from "react";

import { Button, ButtonGroup, ToggleButton } from "react-bootstrap";
import {
  Controller,
  UseControllerProps,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { useQuery } from "@tanstack/react-query";
import AsyncCreatableSelect from "react-select/async-creatable";

import { ServiceOrderType } from "@/_constants/serviceOrder.ts";
import cross from "@/assets/svg/cross.svg";
import Loader from "@/common/components/Loader.tsx";
import ModalDwellingInfo from "@/common/components/ModalDwellingInfo";
import DwellingDetailBadgesComponent from "@/modules/dwelling/components/DwellingDetailBadgesComponent";
import { IDwelling } from "@/modules/dwelling/reducers";
import { DWELLINGS } from "@/modules/dwelling/routes/index.ts";
import { getDwellingsAddress } from "@/modules/dwelling/services";
import { useServiceInventoryStore } from "@/modules/serviceOrdering/store/ServiceInventoryStore";
import history from "@/routes/history";

// Temporary component

interface Props {
  serviceDiagnosticType: ServiceOrderType;
}
const ServiceOrderingPropertyPPPTForm: FC<UseControllerProps & Props> = ({
  name,
  rules,
  serviceDiagnosticType,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const dwelling = useWatch({ name: "dwelling" });
  const filled = getValues(serviceDiagnosticType);

  // Combine all store accesses into a single call to prevent stale values and multiple subscriptions
  const {
    selectedAddress,
    setSelectedAddress,
    setAddressFilter,
    setShowModalDwellingExpress,
    setDwellingExpressAddress,
  } = useServiceInventoryStore((state) => ({
    selectedAddress: state.selectedAddress,
    setSelectedAddress: state.setSelectedAddress,
    setAddressFilter: state.setAddressFilter,
    setShowModalDwellingExpress: state.setShowModalDwellingExpress,
    setDwellingExpressAddress: state.setDwellingExpressAddress,
  }));

  const [showModalDwellingInfo, setModalDwellingInfo] = useState(false);
  const [nbTotalDwellings, setNbTotalDwellings] = useState(0);

  const queryDefaultOptions = useQuery({
    queryKey: ["dwellings", "defaultOptions"],
    queryFn: () => getDwellingsAddress(""),
    select: (data) => {
      return data.dwellings.reduce(
        (acc: { value: IDwelling; label: string }[], property: IDwelling) => {
          return [
            ...acc,
            {
              value: property,
              label: property?.addressLabel,
            },
          ];
        },
        []
      );
    },
  });

  const loadOptionsHandler = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    setAddressFilter(inputValue);
    getDwellingsAddress(inputValue).then((response) => {
      setNbTotalDwellings(response.count);
      callback(
        response.dwellings.map((property: IDwelling) => {
          return {
            value: property,
            label: property?.addressLabel,
          };
        })
      );
    });
  };

  useEffect(() => {
    setValue(name, selectedAddress);
  }, [selectedAddress, name, setValue]);

  return (
    <div className="serviceOrdering_property">
      <div className="serviceOrdering-subtitle">Bien Immobilier</div>
      <div className="serviceOrdering-text">
        Sélectionnez un bien existant ou tapez l'adresse du bien à ajouter
        {rules && <span className="form-input_label-required">&nbsp;*</span>}
      </div>
      {queryDefaultOptions.isLoading ? (
        <Loader />
      ) : (
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState }) => {
            return (
              <div className="serviceOrdering_property-form">
                <div className="serviceOrdering_property-form_select">
                  <AsyncCreatableSelect
                    onCreateOption={() => {}}
                    instanceId={name}
                    inputId={name}
                    defaultInputValue={field?.value?.addressLabel}
                    styles={{
                      control: (baseStyles, _state) => ({
                        ...baseStyles,
                        borderColor: fieldState.error ? "red" : "grey",
                      }),
                    }}
                    defaultOptions={queryDefaultOptions.data}
                    loadOptions={loadOptionsHandler}
                    onChange={(value: any) => {
                      field.onChange(value.value);
                      setSelectedAddress(value.value);
                    }}
                    formatOptionLabel={(option: any) => (
                      <div>
                        {option.value?.id ? (
                          <>
                            <div>{option.value?.addressLabel}</div>
                            <DwellingDetailBadgesComponent
                              dwelling={option.value}
                              viewMore={false}
                            />
                          </>
                        ) : (
                          <>
                            <span
                              onClick={() => history.push(DWELLINGS)}
                              className="badge badge-primary-light mb-4"
                            >
                              Voir les {nbTotalDwellings} autres résultats
                            </span>
                            <Button
                              variant="outline-primary"
                              onClick={() => {
                                setDwellingExpressAddress(option.value);
                                setShowModalDwellingExpress(true);
                              }}
                            >
                              <i className="bi bi-plus-lg" />
                              Ajouter {option.value} à mon portefeuille
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                    placeholder={
                      "Rechercher le bien par adresse, bailleur ou nom de l'ancien occupant"
                    }
                    noOptionsMessage={() => "Aucune adresse correspondante"}
                    loadingMessage={() => "Recherche..."}
                    cacheOptions
                  />
                  {fieldState.error && (
                    <span className="serviceOrdering-text serviceOrdering-text-error">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              </div>
            );
          }}
          rules={rules}
        />
      )}

      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div className={"show-address"}>
          {selectedAddress?.addressLabel && (
            <div className="d-flex flex-row mb-2">
              {`${selectedAddress?.addressLabel}`}&nbsp;
              <div
                className="searchBarre_right-icon"
                onClick={() => {
                  setSelectedAddress(undefined);
                }}
              >
                <img src={cross} alt="Search Icon" />
              </div>
            </div>
          )}
          {dwelling && (
            <DwellingDetailBadgesComponent
              dwelling={dwelling}
              viewMore={true}
              setShowModal={setModalDwellingInfo}
            />
          )}
        </div>
        {filled && (
          <div className="d-flex justify-content-end align-items-center gap-3">
            <label className="form-input form-input_label">Le bien est :</label>
            <Controller
              control={control}
              name={`${serviceDiagnosticType}.isOccupied`}
              defaultValue={false}
              render={({ field: { onChange, value } }) => {
                return (
                  <ButtonGroup>
                    <ToggleButton
                      id={"ToggleButton_service_appointment_vacant"}
                      type="checkbox"
                      variant="outline-primary"
                      checked={value === false}
                      onClick={() => onChange(false)}
                      value={value}
                    >
                      Vacant
                    </ToggleButton>
                    <ToggleButton
                      id={"ToggleButton_service_appointment_occupe"}
                      type="checkbox"
                      variant="outline-primary"
                      checked={value === true}
                      onClick={() => onChange(true)}
                      value={value}
                    >
                      Occupé
                    </ToggleButton>
                  </ButtonGroup>
                );
              }}
            />
          </div>
        )}
      </div>
      <ModalDwellingInfo
        show={showModalDwellingInfo}
        dwellingInfo={dwelling}
        setShow={setModalDwellingInfo}
      />
    </div>
  );
};

export default ServiceOrderingPropertyPPPTForm;
